import React, { useState, useEffect, useCallback } from 'react';
import { getWatchBoard, ackWatchAlert, resolveWatchAlert, getWatchConfig, setWatchConfig, testWatchPush, getWatchHandoff, setTaskDone, setTaskOwner } from '../truckmateStore';
import { playOpen } from '../clickSound';
import { isDispatcher } from '../traccar';

// ---- Watchtower: Priority 1 / Critical, watched by the server 24/7 ----------
// The server checks every active trip once a minute (reefer, late risk, HOS,
// stopped/breakdown, tracking, engine codes) and keeps this list. Criticals push
// to the fleet managers' TagAlong app until someone taps "I'm on it".

const PANEL = '#FFFFFF', PANEL2 = '#F1F5F9', LINE = '#E2E8F0';
const INK = '#172033', INK2 = '#64748B', INK3 = '#94A3B8';
const NEON = '#1677FF', GREEN = '#10B981', AMBER = '#F59E0B', RED = '#EF4444';
const card = { background: PANEL, border: `1px solid ${LINE}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 12px 28px -20px rgba(15,23,42,.28)' };
const btn = (bg, fg, border) => ({ padding: '6px 11px', borderRadius: '8px', border: `1px solid ${border || bg}`, background: bg, color: fg, fontSize: '12px', fontWeight: 700, cursor: 'pointer' });
const SEV = { critical: { c: RED, label: 'CRITICAL' }, warning: { c: AMBER, label: 'WARNING' }, info: { c: INK3, label: 'INFO' } };

// Three lists, so the urgent ones aren't buried under the paperwork. Pure.
const PAPERWORK = { 'ratecon-missing': 'Rate cons missing', 'handling-pending': 'Handling sign-offs pending', 'sheet-mismatch': 'Trip sheet differences', 'appt-missing': 'Appointments missing', 'oc-info-missing': 'Outside carrier info missing' };
export const alertTab = (a) => (a.severity === 'critical' ? 'now' : PAPERWORK[a.code] ? 'paperwork' : 'today');
// Every open to-do from emails, urgent first, then oldest. Pure.
export const openTodos = (trips = []) => trips.flatMap((t) => (t.tasks || []).filter((x) => !x.done).map((x) => ({ ...x, trip: t.tripNumber, unit: t.powerUnit }))).sort((a, b) => (b.urgency === 'urgent') - (a.urgency === 'urgent') || Date.parse(a.at) - Date.parse(b.at));
// Paperwork by type, biggest pile first. Pure.
export const paperworkGroups = (alerts = []) => Object.entries(PAPERWORK).map(([code, label]) => ({ code, label, items: alerts.filter((a) => a.code === code) })).filter((g) => g.items.length).sort((a, b) => b.items.length - a.items.length);
const TABS = [['now', 'Do now'], ['today', 'Today'], ['paperwork', 'Paperwork']];

const ago = (ms) => {
  if (!ms) return '—';
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ${m % 60}m ago` : `${Math.floor(h / 24)}d ago`;
};
const clock = (ms) => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function handoffText(h) {
  const lines = [`SHIFT HANDOFF — ${new Date(h.generatedAt).toLocaleString()}`, `Prepared by ${h.by} · ${h.tripCount} active trips watched`, ''];
  lines.push(`OPEN (${h.open.length})`);
  if (!h.open.length) lines.push('  Nothing open. ✅');
  h.open.forEach((a) => lines.push(`  [${(SEV[a.severity] || SEV.info).label}] ${a.unit ? `Truck ${a.unit} · ` : ''}${a.trip ? `Load ${a.trip} · ` : ''}${a.title}`,
    `      ${a.detail}`, `      ${a.ack ? `Owner: ${a.ack.by} since ${clock(a.ack.at)}` : 'NO OWNER — needs someone'} · open ${ago(a.openedAt)}`));
  lines.push('', `RESOLVED LAST 12H (${h.resolved.length})`);
  h.resolved.forEach((a) => lines.push(`  ${a.unit ? `Truck ${a.unit} · ` : ''}${a.title} — ${a.resolvedBy === 'auto' ? 'cleared on its own' : `closed by ${a.resolvedBy}`} at ${clock(a.resolvedAt)}${a.note ? ` ("${a.note}")` : ''}`));
  return lines.join('\n');
}

function Settings({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => getWatchConfig().then(setCfg).catch((e) => setMsg(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const emails = cfg ? cfg.recipients.map((r) => r.email) : [];
  const save = async (list) => { setMsg(''); try { await setWatchConfig({ recipients: list }); await load(); } catch (e) { setMsg(e.message); } };
  const test = async () => {
    setMsg('Sending…');
    try {
      const r = await testWatchPush();
      const reached = (r.sent || []).filter((x) => x.phones > 0).length;
      setMsg(!r.enabled ? 'Push is not configured on the server (APNs keys).' : `Test sent — reached ${reached} of ${emails.length} people.`);
    } catch (e) { setMsg(e.message); }
  };
  return (
    <div style={{ ...card, padding: '14px', marginBottom: '12px', borderLeft: `3px solid ${NEON}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{ fontSize: '13px', fontWeight: 800, color: INK }}>🔔 Who gets Priority 1 alerts on their phone</div>
        <button onClick={onClose} style={btn(PANEL, INK2, LINE)}>Done</button>
      </div>
      <div style={{ fontSize: '12px', color: INK2, marginBottom: '10px' }}>
        Same list as <b>🔔 Push notifications → Priority 1</b> (admins can set every push type there). Each person must be signed in to the <b>TagAlong app</b> on their iPhone with this email and allow notifications. Critical alerts repeat every {cfg ? cfg.escalateMin : 15} min until someone taps “I’m on it”.
      </div>
      {!cfg ? <div style={{ fontSize: '12px', color: INK3 }}>Loading…</div> : (
        <>
          {cfg.recipients.length === 0 && <div style={{ fontSize: '12px', color: AMBER, fontWeight: 700, marginBottom: '8px' }}>Nobody is on the list yet — alerts only show on this screen.</div>}
          {cfg.recipients.map((r) => (
            <div key={r.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: `1px solid ${LINE}`, fontSize: '13px' }}>
              <span style={{ color: INK, fontWeight: 600 }}>{r.email}</span>
              <span style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '11.5px', fontWeight: 700, color: r.phones ? GREEN : RED }}>{r.phones ? `📱 ${r.phones} phone${r.phones === 1 ? '' : 's'}` : '⚠️ no phone signed in'}</span>
                <button onClick={() => save(emails.filter((e) => e !== r.email))} style={btn(PANEL, RED, LINE)}>Remove</button>
              </span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            {cfg.me && !emails.includes(cfg.me.toLowerCase()) && <button onClick={() => save([...emails, cfg.me])} style={btn(NEON, '#fff')}>+ Add me ({cfg.me})</button>}
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="fleet.manager@email.com"
              style={{ flex: '1 1 200px', padding: '6px 10px', border: `1px solid ${LINE}`, borderRadius: '8px', fontSize: '12.5px' }} />
            <button onClick={() => { if (email.trim()) { save([...emails, email.trim()]); setEmail(''); } }} style={btn(PANEL, NEON, NEON)}>Add</button>
            <button onClick={test} disabled={!emails.length} style={btn(PANEL, INK, LINE)}>Send test alert</button>
          </div>
        </>
      )}
      {msg && <div style={{ fontSize: '12px', color: INK2, marginTop: '8px' }}>{msg}</div>}
    </div>
  );
}

export default function WatchtowerPanel({ onOpenTrip, onCounts, trips = [], onChanged = null }) {
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [tab, setTab] = useState('now');
  const [handoff, setHandoff] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const b = await getWatchBoard();
      setBoard(b); setErr('');
      if (onCounts) onCounts(b.counts);
    } catch (e) { setErr(e.message || 'Could not reach the Watchtower.'); }
  }, [onCounts]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const act = async (id, fn) => {
    setBusy(id);
    try { await fn(); await load(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const openHandoff = async () => {
    playOpen();
    try { setHandoff(handoffText(await getWatchHandoff())); setCopied(false); } catch (e) { setErr(e.message); }
  };

  const open = board ? board.open : [];
  const todos = openTodos(trips);
  // a to-do already shows in Today with its own buttons, so its alert would only say it twice
  const listed = open.filter((a) => !(a.code === 'email-todo' && a.severity !== 'critical'));
  const inTab = (k) => listed.filter((a) => alertTab(a) === k);
  const counts = { now: inTab('now').length, today: inTab('today').length + todos.length, paperwork: inTab('paperwork').length };
  const shown = inTab(tab);
  const checkedAgo = board && board.lastRun ? Math.round((Date.now() - board.lastRun) / 60000) : null;
  const watching = checkedAgo != null && checkedAgo <= 3;

  return (
    <div>
      {/* status strip */}
      <div style={{ ...card, padding: '12px 14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 260px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: watching ? GREEN : AMBER, boxShadow: `0 0 8px ${watching ? GREEN : AMBER}` }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: INK }}>{watching ? 'Watchtower is watching' : 'Watchtower not running yet'}</div>
            <div style={{ fontSize: '11.5px', color: INK2 }}>
              {board && board.lastRun ? `${board.tripCount} trips checked ${ago(board.lastRun)} · every minute, 24/7` : 'Waiting for the first check (runs every minute once deployed).'}
            </div>
          </div>
        </div>
        {[[board ? board.counts.critical : 0, 'Critical', RED], [board ? board.counts.warning : 0, 'Warning', AMBER], [board ? board.counts.unacked : 0, 'No owner', RED]].map(([n, l, c]) => (
          <div key={l} style={{ textAlign: 'center', minWidth: '64px' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: n ? c : INK3 }}>{n}</div>
            <div style={{ fontSize: '9.5px', fontWeight: 800, color: INK3, textTransform: 'uppercase', letterSpacing: '.4px' }}>{l}</div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={openHandoff} style={btn(PANEL, INK, LINE)}>📋 Shift handoff</button>
          {!isDispatcher() && <button onClick={() => { playOpen(); setShowSettings((v) => !v); }} style={btn(PANEL, NEON, NEON)}>🔔 Alert settings</button>}
        </div>
      </div>

      {showSettings && <Settings onClose={() => setShowSettings(false)} />}
      {err && <div style={{ ...card, padding: '10px 14px', marginBottom: '12px', color: '#B91C1C', fontSize: '12.5px', borderLeft: `3px solid ${RED}` }}>{err}</div>}

      {handoff && (
        <div style={{ ...card, padding: '14px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: INK }}>📋 Shift handoff</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { try { navigator.clipboard.writeText(handoff); setCopied(true); } catch { /* ignore */ } }} style={btn(NEON, '#fff')}>{copied ? 'Copied ✓' : 'Copy'}</button>
              <button onClick={() => setHandoff('')} style={btn(PANEL, INK2, LINE)}>Close</button>
            </div>
          </div>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', color: INK, background: PANEL2, borderRadius: '10px', padding: '12px', maxHeight: '360px', overflow: 'auto' }}>{handoff}</pre>
        </div>
      )}

      <div role="tablist" aria-label="Needs attention" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '4px 2px 10px' }}>
        {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={{ ...btn(tab === k ? INK : PANEL, tab === k ? '#fff' : INK, tab === k ? INK : LINE), fontSize: '13px', padding: '8px 14px' }}>{l} ({counts[k]})</button>)}
      </div>

      {board && counts[tab] === 0 && (
        <div style={{ ...card, padding: '26px', textAlign: 'center', color: INK2, fontSize: '13px' }}>✅ {tab === 'now' ? 'Nothing critical right now.' : 'All clear here.'}</div>
      )}

      {tab === 'today' && todos.map((t) => (
        <div key={`${t.trip}-${t.id}`} style={{ ...card, padding: '12px 14px', marginBottom: '10px', borderLeft: `4px solid ${t.urgency === 'urgent' ? RED : NEON}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: '1 1 320px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: INK }}>📧 {t.title}{t.urgency === 'urgent' ? <span style={{ color: RED, fontSize: '11px' }}> urgent</span> : null}</div>
              <div style={{ fontSize: '12px', color: INK2, marginTop: '4px' }}>{[t.unit && `Truck ${t.unit}`, t.trip && `Load ${t.trip}`, t.from && `From ${t.from}`, t.at && ago(Date.parse(t.at))].filter(Boolean).join(' · ')}</div>
              {t.owner && <div style={{ fontSize: '11px', color: GREEN, fontWeight: 700, marginTop: '6px' }}>✓ {t.owner.by} is on it</div>}
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {!t.owner && <button disabled={busy === t.id} onClick={() => act(t.id, async () => { await setTaskOwner(t.trip, t.id); if (onChanged) onChanged(); })} style={btn(RED, '#fff')}>I’m on it</button>}
              <button disabled={busy === t.id} onClick={() => act(t.id, async () => { await setTaskDone(t.trip, t.id, true); if (onChanged) onChanged(); })} style={btn(PANEL, GREEN, GREEN)}>Done</button>
              {onOpenTrip && <button onClick={() => { playOpen(); onOpenTrip(t.trip); }} style={btn(PANEL, NEON, LINE)}>Open load →</button>}
            </div>
          </div>
        </div>
      ))}

      {tab === 'paperwork' && paperworkGroups(shown).map((g) => (
        <details key={g.code} style={{ ...card, padding: '12px 14px', marginBottom: '10px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 800, color: INK }}>{g.label} ({g.items.length})</summary>
          {g.items.map((a) => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${LINE}`, marginTop: '8px' }}>
              <div style={{ minWidth: 0, flex: '1 1 280px', fontSize: '12.5px', color: INK }}><b>{[a.unit && `Truck ${a.unit}`, a.trip && `Load ${a.trip}`].filter(Boolean).join(' · ')}</b><div style={{ color: INK2 }}>{a.title} · open {ago(a.openedAt)}</div></div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button disabled={busy === a.id} onClick={() => { const note = window.prompt('What was done? (saved with the alert)', ''); if (note !== null) act(a.id, () => resolveWatchAlert(a.id, note)); }} style={btn(PANEL, GREEN, GREEN)}>Resolve</button>
                {a.trip && onOpenTrip && <button onClick={() => { playOpen(); onOpenTrip(a.trip); }} style={btn(PANEL, NEON, LINE)}>Open load →</button>}
              </div>
            </div>
          ))}
        </details>
      ))}

      {tab !== 'paperwork' && shown.map((a) => {
        const s = SEV[a.severity] || SEV.info;
        return (
          <div key={a.id} style={{ ...card, padding: '12px 14px', marginBottom: '10px', borderLeft: `4px solid ${s.c}`, background: !a.ack && a.severity === 'critical' ? 'rgba(239,68,68,.05)' : PANEL }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', fontWeight: 800, color: '#fff', background: s.c, borderRadius: '5px', padding: '2px 6px' }}>{s.label}</span>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: INK }}>{a.title}</span>
                </div>
                <div style={{ fontSize: '12px', color: INK2, marginTop: '4px' }}>
                  {[a.unit && `Truck ${a.unit}`, a.trip && `Load ${a.trip}`, a.driver && `Driver ${a.driver}`, a.trailer && `Trailer ${a.trailer}`, a.lane].filter(Boolean).join(' · ')}
                </div>
                <div style={{ fontSize: '12.5px', color: INK, marginTop: '6px' }}>{a.detail}</div>
                <div style={{ fontSize: '11px', color: INK3, marginTop: '6px' }}>
                  Open {ago(a.openedAt)}{a.pushes ? ` · pushed ${a.pushes}×` : ''} · {a.ack ? <span style={{ color: GREEN, fontWeight: 700 }}>✓ {a.ack.by} is on it ({clock(a.ack.at)})</span> : <span style={{ color: RED, fontWeight: 700 }}>No owner yet</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {!a.ack && <button disabled={busy === a.id} onClick={() => { playOpen(); act(a.id, () => ackWatchAlert(a.id)); }} style={btn(RED, '#fff')}>I’m on it</button>}
                <button disabled={busy === a.id} onClick={() => { const note = window.prompt('What was done? (saved with the alert)', ''); if (note !== null) act(a.id, () => resolveWatchAlert(a.id, note)); }} style={btn(PANEL, GREEN, GREEN)}>Resolve</button>
                {a.trip && onOpenTrip && <button onClick={() => { playOpen(); onOpenTrip(a.trip); }} style={btn(PANEL, NEON, LINE)}>Open load →</button>}
              </div>
            </div>
          </div>
        );
      })}

      {board && board.resolved.length > 0 && (
        <div style={{ marginTop: '14px' }}>
          <button onClick={() => setShowResolved((v) => !v)} style={{ ...btn(PANEL, INK2, LINE), marginBottom: '8px' }}>
            {showResolved ? '▾' : '▸'} Resolved in the last 24h ({board.resolved.length})
          </button>
          {showResolved && board.resolved.map((a) => (
            <div key={`${a.id}-${a.resolvedAt}`} style={{ fontSize: '12px', color: INK2, padding: '6px 10px', borderTop: `1px solid ${LINE}` }}>
              <span style={{ color: GREEN, fontWeight: 800 }}>✓</span> {a.unit ? `Truck ${a.unit} · ` : ''}{a.title} — {a.resolvedBy === 'auto' ? 'cleared on its own' : `closed by ${a.resolvedBy}`} {ago(a.resolvedAt)}{a.note ? ` · “${a.note}”` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
